/**
 * « Mon profil → Notifications » : push / email par catégorie (migration 0284)
 * + visibilité de ma progression SKILLCARDS pour mes camarades (0285, élèves).
 */

import { cn } from '@jeraisonne/ui';
import {
  categoriesForRole,
  preferenceFor,
  useNotificationPreferences,
  usePeerVisibility,
  useSetNotificationPreference,
  useSetPeerVisibility,
} from '@/lib/notification-preferences';

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex h-11 w-12 shrink-0 items-center justify-center disabled:opacity-50"
    >
      <span
        className={cn(
          'relative inline-flex h-6 w-11 rounded-full border-2 border-transparent transition-colors',
          checked ? 'bg-accent-400' : 'bg-ink-200',
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </span>
    </button>
  );
}

export function NotificationPreferencesPanel({
  userId,
  role,
}: {
  userId: string;
  role: string | null | undefined;
}) {
  const prefs = useNotificationPreferences(userId);
  const setPref = useSetNotificationPreference(userId);
  const rows = categoriesForRole(role);

  return (
    <div className="space-y-1" data-testid="notification-preferences">
      <div className="text-ink-400 flex justify-end gap-0 text-[11px] font-semibold uppercase">
        <span className="w-12 text-center">Push</span>
        <span className="w-12 text-center">Email</span>
      </div>
      <ul className="divide-ink-100 divide-y">
        {rows.map((row) => {
          const p = preferenceFor(prefs.data, row.category);
          return (
            <li key={row.category} className="flex items-center gap-2 py-1">
              <div className="min-w-0 flex-1">
                <p className="text-ink-900 text-sm font-medium">{row.label}</p>
                <p className="text-ink-400 text-xs">{row.description}</p>
              </div>
              <Toggle
                label={`${row.label} — notifications push`}
                checked={p.push}
                disabled={prefs.isLoading}
                onChange={(push) => setPref.mutate({ ...p, push })}
              />
              <Toggle
                label={`${row.label} — emails`}
                checked={p.email}
                disabled={prefs.isLoading}
                onChange={(email) => setPref.mutate({ ...p, email })}
              />
            </li>
          );
        })}
      </ul>
      {setPref.error && <p className="text-accent-600 text-xs">{setPref.error.message}</p>}
      {role === 'student' && <PeerVisibilityRow userId={userId} />}
    </div>
  );
}

function PeerVisibilityRow({ userId }: { userId: string }) {
  const visible = usePeerVisibility(userId);
  const set = useSetPeerVisibility(userId);
  return (
    <div className="border-ink-100 mt-2 flex items-center gap-2 border-t pt-3">
      <div className="min-w-0 flex-1">
        <p className="text-ink-900 text-sm font-medium">Montrer ma progression à mes camarades</p>
        <p className="text-ink-400 text-xs">
          Tes HORA et ta maîtrise des SKILLCARDS dans « Mon cercle ». Tes profs et tes parents liés
          les voient toujours.
        </p>
      </div>
      <Toggle
        label="Montrer ma progression à mes camarades"
        checked={!!visible.data}
        disabled={visible.isLoading || set.isPending}
        onChange={(v) => set.mutate(v)}
      />
    </div>
  );
}
